import { AT } from '../../../actions/actionTypes';
import { RequestState, Transfer } from './model';
import * as MyKeysApiTransfer from '../../../core/apis/mykeys/transfer';
import { MyKeysPromiseMap } from '../../../core/apis/mykeys/mappings';
import { ErrorDataResponse } from '../../../interfaces';
import useDispatcher from '../../../hooks/useDispatcher';
import useCallStatus from '../../../hooks/useCallStatus';
import { TransferStatus } from './consts';
import { RequestTransferDefault } from './defaults';
import { parsePK, PKey } from '../../devices';
import useTx from './useTx';
import {
  addDeviceToTx,
  clearDeviceFromTx,
  hasDevice,
  setTxId,
  validateTx,
  changeTxStatus,
} from './utils';
import { MessageType } from '../../../core/localization';
import { isDevicePKeyWellFormated, PK_MinLength } from '../../devices';
import * as m from './messages';
import Logger from '../../../libs/logger';  import { LogArea } from '../../../libs/logger/const';

export const useTxRequest = () => {
  const {
    closePanel,
    changeScreen,
    request: { transfer: tx },
  } = useTx();
  const { doAction } = useDispatcher();

  const validateCall = useCallStatus();
  const createCall = useCallStatus();

  const updateTx = (transfer: Transfer) => {
    doAction<RequestState>(AT.DIALOG_TRANSFER_REQUEST_UPDATE_TRANSFER, {
      transfer,
    } as RequestState);
  };

  const updateStatus = (status: TransferStatus) => {
    updateTx({ ...tx, status });
  };

  const reset = () => {
    updateTx({...RequestTransferDefault, id: undefined});
    validateCall.reset();
    createCall.reset();
  };

  const setTxDevices = (devices: PKey[]) => {
    updateTx({ ...tx, devices });
  };

  const isMinLength = (k = '') => k.length >= PK_MinLength; 

  const isValidForm = (key: string, skipBlanks = false) => {
    let errorMsg: MessageType = '';
    let res = true;
    if (!(skipBlanks && key === '')) {
      if (!(parsePK(key) && isDevicePKeyWellFormated(key))) {
        errorMsg = m.msgBadFormatSN;
        res = false;
      }
    }
    validateCall.error(errorMsg);
    return res;
  };

  const checkTx = (transfer: Transfer, onSucceed?: () => void) => {
    Logger.info(['validateRequestTransfer'], LogArea.Redux);
    MyKeysApiTransfer.requestValidateTransfer(transfer)
      .then(MyKeysPromiseMap)
      .then((data: number) => {
        validateCall.success();
        updateTx(validateTx(setTxId(transfer, data)));
        onSucceed?.();
      })
      .catch((err: ErrorDataResponse) => {
        let errMsg = m.msgValidatingError;
        validateCall.error(errMsg);
      });
    validateCall.fetch();
  };

  const addDevice = (key: string, onSucceed?: () => void) => {
    if (!isValidForm(key)) return;

    const deviceId = parsePK(key);
    if (!deviceId) return;

    if (hasDevice(tx, deviceId)) {
      validateCall.error(m.msgDuplicateError);
      return;
    }

    const nextTx = addDeviceToTx(tx, deviceId);
    checkTx(nextTx, onSucceed);
  };

  const clearDevice = (d: PKey) => {
    updateTx(clearDeviceFromTx(tx, d));
  };

  const validate = () => checkTx(tx);

  const createRequest = () => {
    Logger.info(['createRequest'], LogArea.Redux);
    MyKeysApiTransfer.createRequestTransfer(tx)
      .then(MyKeysPromiseMap)
      .then((data: boolean) => {
        if (data) {
          createCall.success();
          updateTx(changeTxStatus(tx, TransferStatus.PENDING));
        } else {
          createCall.error(m.msgCreatingTxError);
        }
      })
      .catch((err: ErrorDataResponse) => {
        createCall.error(m.msgCreatingTxError);
      });
    createCall.fetch();
  };

  return {
    tx,
    reset,
    setTxDevices,
    setStatus: updateStatus,
    addDevice,
    isMinLength,
    isValidForm,
    clearDevice,
    validate,
    validateStatus: validateCall.status,
    createRequest,
    createRequestStatus: createCall.status,
    closePanel,
    changeScreen,
  };
};

export default useTxRequest;
